import React, { useState, useEffect } from 'react';
import { X, Save, Loader2 } from 'lucide-react';
import {
  useActualizarProveedor,
  useCategoriasProveedores,
} from '../../hooks/useProveedores';
import type {
  CreateProveedorRequest,
  Proveedor,
} from '../../services/proveedoresAPI';
import { useToast } from '../../hooks/useToast';

interface EditarProveedorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  proveedor: Proveedor | null;
}

// Tipo extendido para el formulario que incluye observaciones
interface FormData extends CreateProveedorRequest {
  observaciones?: string;
}

// Tipo para errores del formulario
interface FormErrors {
  nombre?: string;
  rut?: string;
  email?: string;
  telefono?: string;
  categoria?: string;
  direccion?: string;
  observaciones?: string;
}

const EditarProveedorModal: React.FC<EditarProveedorModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  proveedor,
}) => {
  // Obtener categorías desde la API
  const { data: categoriasData, isLoading: categoriasLoading } =
    useCategoriasProveedores();
  const categorias = categoriasData?.data || [];

  const [formData, setFormData] = useState<FormData>({
    nombre: '',
    rut: '',
    telefono: '',
    email: '',
    direccion: '',
    categoria: '',
    observaciones: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});

  const { mutate: actualizarProveedor, isPending } = useActualizarProveedor();
  const { error: showToastError } = useToast();

  // Establecer la primera categoría disponible cuando se carguen (solo si no hay proveedor)
  useEffect(() => {
    if (categorias.length > 0 && !proveedor && !formData.categoria) {
      setFormData((prev) => ({ ...prev, categoria: categorias[0] }));
    }
  }, [categorias, proveedor, formData.categoria]);

  // Rellenar el formulario cuando se abra el modal con un proveedor
  useEffect(() => {
    if (isOpen && proveedor) {
      setFormData({
        nombre: proveedor.nombre || '',
        rut: proveedor.rut || '',
        telefono: proveedor.telefono || '',
        email: proveedor.email || '',
        direccion: proveedor.direccion || '',
        categoria: proveedor.categoria || 'otros',
        observaciones: '', // Las observaciones no están en el modelo actual
      });
      setErrors({});
    }
  }, [isOpen, proveedor]);

  // Validación del formulario
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.nombre.trim()) {
      newErrors.nombre = 'El nombre es requerido';
    }

    if (!formData.rut.trim()) {
      newErrors.rut = 'El RUT es requerido';
    } else {
      // Validación básica de RUT (formato chileno con o sin puntos)
      const rutPattern = /^[0-9]{1,2}\.?[0-9]{3}\.?[0-9]{3}[-|‐]{1}[0-9kK]{1}$/;
      if (!rutPattern.test(formData.rut.trim())) {
        newErrors.rut =
          'Formato de RUT inválido (ej: 12.345.678-9 o 12345678-9)';
      }
    }

    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido';
    } else {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(formData.email.trim())) {
        newErrors.email = 'Formato de email inválido';
      }
    }

    if (!formData.categoria) {
      newErrors.categoria = 'La categoría es requerida';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Manejar envío del formulario
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !proveedor) {
      // Mostrar toast con los errores
      if (!proveedor) return;

      const erroresArray = Object.entries(errors)
        .filter(([_, value]) => value)
        .map(([key, value]) => `${key}: ${value}`);

      if (erroresArray.length > 0) {
        showToastError(
          'Faltan datos requeridos',
          erroresArray.join('\n'),
          6000
        );
      }
      return;
    }

    // Preparar datos para envío (sin observaciones para la API)
    const dataToSubmit: Partial<CreateProveedorRequest> = {
      nombre: formData.nombre.trim(),
      rut: formData.rut.trim().replace(/\./g, ''), // Eliminar puntos del RUT
      categoria: formData.categoria,
      telefono: formData.telefono?.trim() || '',
      email: formData.email?.trim() || '',
      direccion: formData.direccion?.trim() || '',
    };

    actualizarProveedor(
      { id: proveedor.id, data: dataToSubmit },
      {
        onSuccess: () => {
          handleClose();
          onSuccess();
        },
        onError: (error: any) => {
          console.error('Error al actualizar proveedor:', error);
          // El hook ya maneja el toast de error
        },
      }
    );
  };

  // Manejar cierre del modal
  const handleClose = () => {
    if (!isPending) {
      setFormData({
        nombre: '',
        rut: '',
        telefono: '',
        email: '',
        direccion: '',
        categoria: categorias.length > 0 ? categorias[0] : '',
        observaciones: '',
      });
      setErrors({});
      onClose();
    }
  };

  // Manejar cambios en el formulario
  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev: FormData) => ({ ...prev, [field]: value }));

    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[field]) {
      setErrors((prev: FormErrors) => ({ ...prev, [field]: undefined }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Editar Proveedor
          </h2>
          <button
            onClick={handleClose}
            disabled={isPending}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Formulario */}
        <form
          onSubmit={handleSubmit}
          className="overflow-y-auto max-h-[calc(90vh-120px)]"
        >
          <div className="px-6 py-4 space-y-6">
            {/* Información básica */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Nombre */}
              <div>
                <label
                  htmlFor="nombre"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Nombre del Proveedor *
                </label>
                <input
                  type="text"
                  id="nombre"
                  value={formData.nombre}
                  onChange={(e) => handleInputChange('nombre', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.nombre
                      ? 'border-red-300 dark:border-red-600'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                  placeholder="Ej: Distribuidora San Juan"
                  disabled={isPending}
                />
                {errors.nombre && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                    {errors.nombre}
                  </p>
                )}
              </div>

              {/* RUT */}
              <div>
                <label
                  htmlFor="rut"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  RUT *
                </label>
                <input
                  type="text"
                  id="rut"
                  value={formData.rut}
                  onChange={(e) => handleInputChange('rut', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.rut
                      ? 'border-red-300 dark:border-red-600'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                  placeholder="12345678-9"
                  disabled={isPending}
                />
                {errors.rut && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                    {errors.rut}
                  </p>
                )}
              </div>
            </div>

            {/* Contacto */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Teléfono */}
              <div>
                <label
                  htmlFor="telefono"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Teléfono
                </label>
                <input
                  type="text"
                  id="telefono"
                  value={formData.telefono}
                  onChange={(e) =>
                    handleInputChange('telefono', e.target.value)
                  }
                  className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.telefono
                      ? 'border-red-300 dark:border-red-600'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                  placeholder="+56 2 2345 6789"
                  disabled={isPending}
                />
                {errors.telefono && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                    {errors.telefono}
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.email
                      ? 'border-red-300 dark:border-red-600'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                  placeholder="contacto@proveedor.com"
                  disabled={isPending}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                    {errors.email}
                  </p>
                )}
              </div>
            </div>

            {/* Categoría */}
            <div>
              <label
                htmlFor="categoria"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Categoría *
              </label>
              <select
                id="categoria"
                value={formData.categoria}
                onChange={(e) => handleInputChange('categoria', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.categoria
                    ? 'border-red-300 dark:border-red-600'
                    : 'border-gray-300 dark:border-gray-600'
                }`}
                disabled={isPending || categoriasLoading}
              >
                {categoriasLoading ? (
                  <option value="">Cargando categorías...</option>
                ) : categorias.length > 0 ? (
                  categorias.map((categoria) => (
                    <option key={categoria} value={categoria}>
                      {categoria.charAt(0).toUpperCase() +
                        categoria.slice(1).replace(/_/g, ' ')}
                    </option>
                  ))
                ) : (
                  <option value="otros">Otros</option>
                )}
              </select>
              {errors.categoria && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {errors.categoria}
                </p>
              )}
            </div>

            {/* Dirección */}
            <div>
              <label
                htmlFor="direccion"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Dirección
              </label>
              <input
                type="text"
                id="direccion"
                value={formData.direccion}
                onChange={(e) => handleInputChange('direccion', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.direccion
                    ? 'border-red-300 dark:border-red-600'
                    : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder="Av. Libertador 1234, Santiago"
                disabled={isPending}
              />
              {errors.direccion && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {errors.direccion}
                </p>
              )}
            </div>

            {/* Observaciones */}
            <div>
              <label
                htmlFor="observaciones"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Observaciones
              </label>
              <textarea
                id="observaciones"
                rows={3}
                value={formData.observaciones}
                onChange={(e) =>
                  handleInputChange('observaciones', e.target.value)
                }
                className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.observaciones
                    ? 'border-red-300 dark:border-red-600'
                    : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder="Información adicional sobre el proveedor..."
                disabled={isPending}
              />
              {errors.observaciones && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {errors.observaciones}
                </p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3 bg-gray-50 dark:bg-gray-700/50">
            <button
              type="button"
              onClick={handleClose}
              disabled={isPending}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-500 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-medium transition-colors disabled:cursor-not-allowed"
            >
              {isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Actualizando...</span>
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  <span>Guardar Cambios</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditarProveedorModal;
